import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, finalize, Observable, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  DeliveryManifestReport,
  DeliveryManifestReportRequest,
  DeliveryManifestRoutes,
  DriverLoadReportReport,
  DriverLoadReportRequest,
  DriverLoadRoutes,
  GetDryIceResponse,
  GetPickupManifest,
  LoadDiscrepancyReport,
  LoadDiscrepancyReportRequest,
  LoadDiscrepancyRoutes,
  PickupManifestRoutes,
  SendAgainReport,
  SendAgainReportRequest,
  SendAgainRoutes,
} from 'src/app/core/models/reports/reports.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class OnRoadOperationsService {
  baseUrl = environment.apiUrl.concat('report-management/');
  constructor(
    private http: HttpClient,
    private commonUtils: CommonUtils,
    private errorHandler: ErrorhandleService,
  ) {}

  /**
   *Get a specific DeliveryManifest Routes based on the passed in location code
   *
   * @param {string} locationCode
   * @return {*}  {Observable<DeliveryManifestRoutes>}
   * @memberof ReportsService
   */
  getDeliveryManifestRoutes(
    locationCode: string,
  ): Observable<DeliveryManifestRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<DeliveryManifestRoutes>(
        this.baseUrl.concat('delivery-manifest-report/routes'),
        {
          params: { locationCode: locationCode },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<DeliveryManifestRoutes>(
            error,
            error.status,
            'Report Service: Get Delivery Manifest Routes',
            BANNER_TYPE.ROUTES_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific DeliveryManifest Report based on the request
   *
   * @param {DeliveryManifestReportRequest} request
   * @return {*}  {Observable<DeliveryManifestReport>}
   * @memberof ReportsService
   */
  getDeliveryManifestReport(
    request: DeliveryManifestReportRequest,
  ): Observable<DeliveryManifestReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<DeliveryManifestReport>(
        this.baseUrl.concat('delivery-manifest-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<DeliveryManifestReport>(
            error,
            error.status,
            'Report Service: Get Delivery Manifest Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific Driver Load Routes based on the passed in location code
   *
   * @param {string} locationCode
   * @return {*}  {Observable<DriverLoadRoutes>}
   * @memberof ReportsService
   */
  getDriverLoadRoutes(locationCode: string): Observable<DriverLoadRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<DriverLoadRoutes>(this.baseUrl.concat('driver-load-report/routes'), {
        params: { locationCode: locationCode },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<DriverLoadRoutes>(
            error,
            error.status,
            'Report Service: Get Driver Load Routes',
            BANNER_TYPE.ROUTES_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific Driver load Report based on the request
   *
   * @param {DriverLoadReportRequest} request
   * @return {*}  {Observable<DeliveryManifestReport>}
   * @memberof ReportsService
   */
  getDriverLoadReport(
    request: DriverLoadReportRequest,
  ): Observable<DriverLoadReportReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<DriverLoadReportReport>(this.baseUrl.concat('driver-load-report'), {
        params: { ...request },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<DriverLoadReportReport>(
            error,
            error.status,
            'Report Service: Get Driver Load Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific Load Discrepancy Routes based on the passed in location code
   *
   * @param {string} locationCode
   * @return {*}  {Observable<LoadDiscrepancyRoutes>}
   * @memberof ReportsService
   */
  getLoadDiscrepancyRoutes(
    locationCode: string,
  ): Observable<LoadDiscrepancyRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<LoadDiscrepancyRoutes>(
        this.baseUrl.concat('load-discrepancy-report/routes'),
        {
          params: { locationCode: locationCode },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<LoadDiscrepancyRoutes>(
            error,
            error.status,
            'Report Service: Get Load Discrepancy Routes',
            BANNER_TYPE.ROUTES_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific Load Discrepancy Report based on the request
   *
   * @param {LoadDiscrepancyReportRequest} request
   * @return {*}  {Observable<DeliveryManifestReport>}
   * @memberof ReportsService
   */
  getLoadDiscrepancyReport(
    request: LoadDiscrepancyReportRequest,
  ): Observable<LoadDiscrepancyReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<LoadDiscrepancyReport>(
        this.baseUrl.concat('load-discrepancy-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<LoadDiscrepancyReport>(
            error,
            error.status,
            'Report Service: Load Discrepancy Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific sendAgain Routes based on the passed in location code
   *
   * @param {string} locationcode
   * @return {*}  {Observable<SendAgainRoutes>}
   * @memberof ReportsService
   */
  getSendAgainRoutes(locationcode: string): Observable<SendAgainRoutes> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<SendAgainRoutes>(this.baseUrl.concat('send-again-report/routes'), {
        params: { locationcode: locationcode },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<SendAgainRoutes>(
            error,
            error.status,
            'Report Service: Get Send Again Routes',
            BANNER_TYPE.ROUTES_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific SendAgain Report based on the request
   *
   * @param {SendAgainReportRequest} request
   * @return {*}  {Observable<SendAgainReport>}
   * @memberof ReportsService
   */
  getSendAgainReport(
    request: SendAgainReportRequest,
  ): Observable<SendAgainReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<SendAgainReport>(this.baseUrl.concat('send-again-report'), {
        params: { ...request },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<SendAgainReport>(
            error,
            error.status,
            'Report Service: Get Send Again Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get all available routes for Pickup Manifest based on passed in date
   *
   * @param {string} locationCode
   * @return {*}  {Observable<DeliveryManifestRoutes>}
   * @memberof ReportsService
   */
  getPickupManifestRoutes(
    locationCode: string,
  ): Observable<PickupManifestRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<PickupManifestRoutes>(
        this.baseUrl.concat('pickup-manifest-report/routes'),
        {
          params: { locationCode: locationCode },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<PickupManifestRoutes>(
            error,
            error.status,
            'Report Service: Get Pickup Manifest Routes',
            BANNER_TYPE.ROUTES_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get All Pickup Manifest Reports based on passed in data
   *
   * @param {string} locationCode
   * @return {*}  {Observable<DeliveryManifestRoutes>}
   * @memberof ReportsService
   */
  getPickupManifest(
    locationCode: string,
    fromDate: string,
    toDate: string,
    routeId: string,
  ): Observable<GetPickupManifest> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetPickupManifest>(this.baseUrl.concat('pickup-manifest-report'), {
        params: {
          locationCode: locationCode,
          loop: routeId,
          fromDate: fromDate,
          toDate: toDate,
        },
      })
      .pipe(
        take(1),
        filter((value) => !!value),
        catchError((error) => {
          this.errorHandler.handleError<HttpErrorResponse>(
            error,
            error.status,
            'Report Service: Get Pickup Manifest',
          );
          return of();
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get All Pickup Manifest Reports based on passed in data
   *
   * @param {string} locationCode
   * @return {*}  {Observable<DeliveryManifestRoutes>}
   * @memberof ReportsService
   */
  getDryIceReport(
    selectDate: string,
    routeId: string,
  ): Observable<GetDryIceResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetDryIceResponse>(this.baseUrl.concat('dry-ice-report'), {
        params: {
          loop: routeId,
          date: selectDate,
        },
      })
      .pipe(
        take(1),
        filter((value) => !!value),
        catchError((error) => {
          this.errorHandler.handleError<HttpErrorResponse>(
            error,
            error.status,
            'Report Service: Get Dry Ice',
          );
          return of();
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
